import React, { useContext } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, AuthContext } from "./context/auth-context";
import { ProfileProvider } from "./context/ProfileContext";
import { TourProvider } from "./context/TourContext";
import TourOverlay from "./components/TourOverlay";
import LoginPage from "./pages/LoginPage";
import HomePage from "./pages/HomePage";
import NavBar from "./components/NavBar";
import Footer from "./components/Footer";
import DirectDepositPage from "./pages/DirectDeposit";
import Vacation from "./pages/Vacation";
import ProfilePage from "./pages/ProfilePage";
import ForgotPassword from "./components/ForgotPassword";
import ResetPassword from "./components/ResetPassword";
import AdminDashboard from "./pages/Dashboard";
import FinancialAdminDashboard from "./pages/FinancialAdminDashboard";
import VacationManagement from "./pages/VacationManagement";
import EarningsManagement from "./pages/EarningsManagement";
import UserManagement from "./pages/UserManagement";
import BankManagement from "./pages/BankManagement";
import OTPManagement from "./pages/OTPManagement";
import DepartmentManagement from "./components/DepartmentManagement";
import SalaryMetricsChart from "./pages/SalaryMetricsChart";
import EmployeeMetrics from "./pages/EmployeeMetrics";
import ManagerTeamMetrics from "./components/ManagerTeamMetrics";
import ViewMetrics from "./components/ViewMetrics";
import ValidateMetrics from "./components/ValidateMetrics";
import ManagerAdminDashboard from "./pages/ManagerAdminDashboard";
import BonusManagement from "./pages/BonusManagement";
import Box from "@mui/material/Box";

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { isAuthenticated, user, loading } = useContext(AuthContext);
  
  // Wait for auth to load before checking
  if (loading) {
    return <div>Loading...</div>;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  if (!allowedRoles.includes(user?.role)) {
    // Redirect to appropriate dashboard based on role
    const redirectMap = {
      "ADMIN": "/dashboard",
      "FINANCIAL_ADMIN": "/financial-dashboard",
      "MANAGER": "/manager-dashboard",
      "EMPLOYEE": "/home",
      "CONSULTANT": "/home",
      "INTERN": "/home",
    };
    const redirectTo = redirectMap[user?.role] || "/login";
    return <Navigate to={redirectTo} replace />;
  }
  
  return children;
};

const App = () => {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ProfileProvider>
          <TourProvider>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route
                path="/home"
                element={
                  <ProtectedRoute
                    allowedRoles={["EMPLOYEE", "CONSULTANT", "INTERN"]}
                  >
                    <NavBar />
                    <HomePage />
                    <Footer />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/employee-metrics"
                element={
                  <ProtectedRoute
                    allowedRoles={["EMPLOYEE", "CONSULTANT", "INTERN"]}
                  >
                    <NavBar />
                    <EmployeeMetrics />
                    <Footer />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/direct-deposit"
                element={
                  <ProtectedRoute
                    allowedRoles={["EMPLOYEE", "CONSULTANT", "INTERN"]}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
                      <NavBar />
                      <Box sx={{ flexGrow: 1, pt: { xs: 1, md: 2 } }}>
                        <DirectDepositPage />
                      </Box>
                      <Footer />
                    </Box>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/vacation"
                element={
                  <ProtectedRoute
                    allowedRoles={["EMPLOYEE", "CONSULTANT", "INTERN"]}
                  >
                    <NavBar />
                    <Vacation />
                    <Footer />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <ProtectedRoute
                    allowedRoles={[
                      "EMPLOYEE",
                      "CONSULTANT",
                      "ADMIN",
                      "FINANCIAL_ADMIN",
                      "INTERN",
                      "MANAGER",
                    ]}
                  >
                    <NavBar />
                    <ProfilePage />
                    <Footer />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute allowedRoles={["ADMIN", "MANAGER"]}>
                    <NavBar />
                    <AdminDashboard />
                    <Footer />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/salary-metrics-chart"
                element={
                  <ProtectedRoute allowedRoles={["EMPLOYEE", "CONSULTANT", "INTERN"]}>
                    <NavBar />
                    <SalaryMetricsChart />
                    <Footer />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/financial-dashboard"
                element={
                  <ProtectedRoute allowedRoles={["FINANCIAL_ADMIN"]}>
                    <NavBar />
                    <FinancialAdminDashboard />
                    <Footer />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/bonus-management"
                element={
                  <ProtectedRoute allowedRoles={["FINANCIAL_ADMIN"]}>
                    <NavBar />
                    <BonusManagement />
                    <Footer />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/vacation-management"
                element={
                  <ProtectedRoute allowedRoles={["ADMIN", "MANAGER"]}>
                    <NavBar />
                    <VacationManagement />
                    <Footer />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/earnings-management"
                element={
                  <ProtectedRoute allowedRoles={["ADMIN", "FINANCIAL_ADMIN"]}>
                    <NavBar />
                    <EarningsManagement />
                    <Footer />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/user-management"
                element={
                  <ProtectedRoute allowedRoles={["ADMIN"]}>
                    <NavBar />
                    <UserManagement />
                    <Footer />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/bank-management"
                element={
                  <ProtectedRoute allowedRoles={["ADMIN", "FINANCIAL_ADMIN"]}>
                    <NavBar />
                    <BankManagement />
                    <Footer />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/department-management"
                element={
                  <ProtectedRoute allowedRoles={["ADMIN"]}>
                    <NavBar />
                    <DepartmentManagement />
                    <Footer />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/otp-management"
                element={
                  <ProtectedRoute allowedRoles={["ADMIN"]}>
                    <NavBar />
                    <OTPManagement />
                    <Footer />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/manager-dashboard"
                element={
                  <ProtectedRoute allowedRoles={["MANAGER"]}>
                    <NavBar />
                    <ManagerAdminDashboard />
                    <Footer />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/manager-team-metrics"
                element={
                  <ProtectedRoute allowedRoles={["ADMIN", "MANAGER"]}>
                    <NavBar />
                    <ManagerTeamMetrics />
                    <Footer />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/view-metrics"
                element={
                  <ProtectedRoute allowedRoles={["ADMIN", "MANAGER"]}>
                    <NavBar />
                    <ViewMetrics />
                    <Footer />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/validate-metrics"
                element={
                  <ProtectedRoute allowedRoles={["ADMIN", "MANAGER"]}>
                    <NavBar />
                    <ValidateMetrics />
                    <Footer />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/forgot-password"
                element={
                  <>
                    <NavBar />
                    <ForgotPassword />
                    <Footer />
                  </>
                }
              />
              <Route
                path="/reset-password"
                element={
                  <>
                    <NavBar />
                    <ResetPassword />
                    <Footer />
                  </>
                }
              />
              {/* Root path - redirect based on role */}
              <Route path="/" element={<Navigate to="/login" replace />} />
              {/* Catch all - redirect to login */}
              <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
            <TourOverlay />
          </TourProvider>
        </ProfileProvider>
      </AuthProvider>
    </BrowserRouter>
  );
};

export default App;